import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { CheckCircle } from "lucide-react";

interface AnimalChecklistProgressProps {
  checklistType: "readiness" | "evaluation";
  done: number;
  total: number;
  className?: string;
}

const LABELS: Record<"readiness" | "evaluation", string> = {
  readiness: "Adoption Readiness",
  evaluation: "Evaluation",
};

export function AnimalChecklistProgress({
  checklistType,
  done,
  total,
  className,
}: AnimalChecklistProgressProps) {
  const label = LABELS[checklistType];

  if (total === 0) {
    return (
      <p
        className={cn("text-sm text-muted-foreground", className)}
        data-testid={`text-checklist-progress-empty-${checklistType}`}
      >
        No {label.toLowerCase()} checklist items configured
      </p>
    );
  }

  const pct = Math.round((done / total) * 100);
  const allDone = done === total;

  return (
    <div
      className={cn("space-y-1", className)}
      data-testid={`div-checklist-progress-${checklistType}`}
    >
      <div className="flex items-center gap-1.5">
        {allDone && (
          <CheckCircle
            className="h-4 w-4 text-green-600 dark:text-green-400"
            aria-hidden="true"
          />
        )}
        <span
          className={cn(
            "text-sm font-medium",
            allDone ? "text-green-700 dark:text-green-400" : "text-muted-foreground",
          )}
          data-testid={`text-checklist-progress-label-${checklistType}`}
        >
          {label}: {done}/{total} complete
        </span>
      </div>
      <Progress
        value={pct}
        className={cn("h-1.5", allDone && "[&>div]:bg-green-500")}
        data-testid={`progress-checklist-${checklistType}`}
      />
    </div>
  );
}
